/**
 * SurveyNotesPanel Component
 *
 * An inline side panel that displays all notes associated with a survey
 * and provides a form for adding new notes.
 * Uses the generic DetailsSidePanel for layout and close-button behavior.
 * Notes are sorted chronologically (oldest first, newest at the bottom).
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Survey, SurveyNote, addSurveyNote } from "@/services/surveysListService"
import { useAuth } from "@/hooks/useAuth"
import DetailsSidePanel from "@/components/shared/DetailsSidePanel"

/** Props for the SurveyNotesPanel component. */
interface SurveyNotesPanelProps {
  /** The survey whose notes should be displayed. */
  survey: Survey
  /** Callback fired when the panel is closed. */
  onClose: () => void
  /** Callback fired after a note is successfully added, to refresh survey data. */
  onNoteAdded: () => void
}

/**
 * Formats an ISO date string into a human-readable date (date only, no time).
 *
 * @param dateString - An ISO 8601 date string (e.g. "2025-06-01T12:34:56+00:00").
 * @returns A formatted date string like "1 Jun 2025", or the original string if parsing fails.
 */
function formatNoteDate(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    return dateString
  }
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

/**
 * Inline side panel displaying all notes for a survey with an add-note form.
 *
 * Renders the survey's notes sorted by date (oldest first, newest at the bottom)
 * inside a scrollable area. The header and add-note form are pinned (non-scrollable).
 *
 * @param props - Component props.
 * @param props.survey - The survey whose notes are displayed.
 * @param props.onClose - Callback to close the panel.
 * @param props.onNoteAdded - Callback invoked after successful note submission.
 * @returns The rendered notes panel element.
 */
export default function SurveyNotesPanel({
  survey,
  onClose,
  onNoteAdded,
}: SurveyNotesPanelProps): React.JSX.Element {
  const { getAccessToken, user } = useAuth()

  const [noteText, setNoteText] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  /** Ref for the scrollable notes container. */
  const notesContainerRef = useRef<HTMLDivElement>(null)

  /** Notes sorted chronologically (oldest first, newest at bottom). */
  const sortedNotes: SurveyNote[] = useMemo(() => {
    const notes = survey.notes ?? []
    return [...notes].sort((a, b) => {
      return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
    })
  }, [survey.notes])

  // Scroll to bottom on initial render (instant) and smoothly when notes change
  useEffect(() => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop = notesContainerRef.current.scrollHeight
    }
  }, [sortedNotes.length])

  /**
   * Handles form submission to add a new note for the current survey.
   * Calls the addSurveyNote service, then invokes onNoteAdded to refresh data.
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    const trimmedNote = noteText.trim()
    if (!trimmedNote) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const token = await getAccessToken()
      // Use the authenticated user's email as the createdBy value
      const createdBy: string = user?.email ?? user?.name ?? "Unknown"

      await addSurveyNote(token, {
        mapSurveyId: survey.mapSurveyId,
        note: trimmedNote,
        createdBy: createdBy,
      })

      setNoteText("")
      onNoteAdded()
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to add note"
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [noteText, getAccessToken, user, survey.mapSurveyId, onNoteAdded])

  return (
    <DetailsSidePanel
      onClose={onClose}
      className="border"
      header={
        <div className="min-w-0">
          <h3 className="font-medium text-sm leading-snug line-clamp-2 truncate">Notes</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {survey.mapSurveyId} &mdash; {survey.countryName} ({survey.year})
          </p>
        </div>
      }
    >
      {/* Scrollable notes area — only this section scrolls */}
      <div ref={notesContainerRef} className="flex-1 min-h-0 overflow-y-auto p-4">
        {sortedNotes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes for this survey.</p>
        ) : (
          <div className="space-y-3">
            {sortedNotes.map(note => (
              <div
                key={note.createdOn}
                className="rounded-md bg-muted/50 border border-border p-3 space-y-2"
              >
                <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">{note.createdBy}</p>
                  <p className="text-xs text-muted-foreground">{formatNoteDate(note.createdOn)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pinned add-note form at bottom */}
      <div className="shrink-0 border-t border-border p-4 space-y-2">
        <Textarea
          placeholder="Write a new note…"
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          disabled={isSubmitting}
          className="resize-none"
        />
        {submitError && <p className="text-xs text-destructive">{submitError}</p>}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting || !noteText.trim()}
            className={isSubmitting ? "" : "disabled:opacity-100 disabled:cursor-default"}
          >
            {isSubmitting ? "Adding…" : "Add Note"}
          </Button>
        </div>
      </div>
    </DetailsSidePanel>
  )
}
